/**
 * Named periods of the screens' "Período" (a select with shortcuts and a custom range): the
 * days each one covers, as `YYYY-MM-DD` of the local calendar. Weeks run Monday to Sunday.
 */

export const PERIOD_OPTIONS = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  '7d',
  '30d',
  '90d',
  'thisMonth',
  'lastMonth',
  'thisYear',
  '12m',
  'custom',
] as const;

export type PeriodOption = (typeof PERIOD_OPTIONS)[number];

export interface DayRange {
  from: string;
  to: string;
}

/** `YYYY-MM-DD` of a local day. */
export function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

/** Monday of the week of `date`. */
function weekStart(date: Date): Date {
  return addDays(date, -((date.getDay() + 6) % 7));
}

const range = (from: Date, to: Date): DayRange => ({ from: isoDay(from), to: isoDay(to) });

/** The days of a named period, from today; `custom` has none of its own (`null`). */
export function periodRange(option: PeriodOption, today = new Date()): DayRange | null {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (option) {
    case 'today':
      return range(day, day);
    case 'yesterday':
      return range(addDays(day, -1), addDays(day, -1));
    case 'thisWeek':
      return range(weekStart(day), day);
    case 'lastWeek': {
      const start = addDays(weekStart(day), -7);
      return range(start, addDays(start, 6));
    }
    case '7d':
      return range(addDays(day, -6), day);
    case '30d':
      return range(addDays(day, -29), day);
    case '90d':
      return range(addDays(day, -89), day);
    case 'thisMonth':
      return range(new Date(day.getFullYear(), day.getMonth(), 1), day);
    case 'lastMonth':
      return range(
        new Date(day.getFullYear(), day.getMonth() - 1, 1),
        new Date(day.getFullYear(), day.getMonth(), 0),
      );
    case 'thisYear':
      return range(new Date(day.getFullYear(), 0, 1), day);
    case '12m':
      return range(new Date(day.getFullYear() - 1, day.getMonth(), day.getDate() + 1), day);
    case 'custom':
      return null;
  }
}

/** How many days a range covers (both ends included). */
export function daysIn({ from, to }: DayRange): number {
  return (
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
  );
}
